import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
    limit,
    startAfter,
    QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from './config';
import { uploadBase64ImageToCloudinary } from '../services/cloudinaryService';

// Blog data interface
export interface Blog {
    id?: string;
    title: string;
    slug: string; // URL-friendly version of title
    excerpt: string; // Short description
    content: string; // Full blog content (rich text/markdown)
    featuredImage?: string;
    gallery?: string[];
    category: string; // Main category (e.g., "Flutter", "React", "Node.js")
    subcategory?: string; // Subcategory (e.g., "State Management", "UI/UX", "Performance")
    tags: string[];
    language: 'Bengali' | 'English'; // Language filter
    difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
    readingTime: number; // Estimated reading time in minutes
    youtubeVideoId?: string; // YouTube video ID for tutorial
    youtubeVideoTitle?: string;
    youtubeVideoDescription?: string;
    youtubeVideoThumbnail?: string;
    codeRepository?: string; // GitHub repo for the tutorial code
    demoUrl?: string;
    downloadableResources?: {
        title: string;
        url: string;
        type: 'pdf' | 'zip' | 'code' | 'asset';
    }[];
    prerequisites: string[]; // What readers should know before reading
    learningOutcomes: string[]; // What readers will learn
    relatedTopics: string[]; // Related topics/technologies
    seoKeywords: string[];
    metaDescription?: string;
    published: boolean;
    featured?: boolean;
    views?: number;
    likes?: number;
    comments?: number;
    publishedDate?: string; // ISO date string
    lastUpdated?: string;
    author: string;
    authorBio?: string;
    authorImage?: string;
    estimatedDevelopmentTime?: string; // e.g., "2-3 hours"
    targetAudience: string[]; // e.g., ["Flutter Developers", "Beginners", "Mobile App Developers"]
    order?: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

// Blog category interface
export interface BlogCategory {
    id: string;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    subcategories: {
        id: string;
        name: string;
        description?: string;
    }[];
}

// Collection references
const blogsCollection = collection(db, 'blogs');
const blogCategoriesCollection = collection(db, 'blogCategories');

/**
 * Get all blogs with optional filtering and pagination
 */
export const getAllBlogs = async (filters?: {
    category?: string;
    subcategory?: string;
    language?: 'Bengali' | 'English';
    difficulty?: 'Beginner' | 'Intermediate' | 'Advanced';
    published?: boolean;
    featured?: boolean;
    limit?: number;
    lastDoc?: QueryDocumentSnapshot;
}): Promise<{ blogs: Blog[], lastDoc?: QueryDocumentSnapshot }> => {
    try {
        // Temporary fix while indexes are building:
        // If no specific filters are applied, get all docs without ordering and sort client-side
        const hasSpecificFilters = filters && (
            filters.category || 
            filters.subcategory || 
            filters.language || 
            filters.difficulty || 
            filters.featured !== undefined ||
            filters.published !== undefined
        );

        let blogsQuery;
        
        if (!hasSpecificFilters) {
            // No filters - get all documents without ordering to avoid index requirement
            blogsQuery = query(blogsCollection);
        } else {
            // Has filters - use normal query with ordering
            blogsQuery = query(blogsCollection, orderBy('publishedDate', 'desc'));

            // Apply filters
            if (filters?.category) {
                blogsQuery = query(blogsQuery, where('category', '==', filters.category));
            }
            if (filters?.subcategory) {
                blogsQuery = query(blogsQuery, where('subcategory', '==', filters.subcategory));
            }
            if (filters?.language) {
                blogsQuery = query(blogsQuery, where('language', '==', filters.language));
            }
            if (filters?.difficulty) {
                blogsQuery = query(blogsQuery, where('difficulty', '==', filters.difficulty));
            }
            if (filters?.published !== undefined) {
                blogsQuery = query(blogsQuery, where('published', '==', filters.published));
            }
            if (filters?.featured !== undefined) {
                blogsQuery = query(blogsQuery, where('featured', '==', filters.featured));
            }
        }

        // Apply pagination
        if (filters?.lastDoc) {
            blogsQuery = query(blogsQuery, startAfter(filters.lastDoc));
        }
        if (filters?.limit) {
            blogsQuery = query(blogsQuery, limit(filters.limit));
        }

        const querySnapshot = await getDocs(blogsQuery);
        let blogs = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Blog));

        // If no specific filters were applied, sort client-side by publishedDate
        if (!hasSpecificFilters) {
            blogs = blogs.sort((a, b) => 
                new Date(b.publishedDate || 0).getTime() - new Date(a.publishedDate || 0).getTime()
            );
        }

        const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

        return { blogs, lastDoc };
    } catch (error) {
        console.error('Error getting blogs:', error);
        throw error;
    }
};

/**
 * Search blogs by title, content, tags
 */
export const searchBlogs = async (searchTerm: string, filters?: {
    category?: string;
    language?: 'Bengali' | 'English';
    difficulty?: 'Beginner' | 'Intermediate' | 'Advanced';
}): Promise<Blog[]> => {
    try {
        // For a more sophisticated search, you might want to use Algolia or similar
        // For now, we'll do a simple client-side search
        const { blogs } = await getAllBlogs({
            ...filters,
            published: true
        });

        const lowerSearchTerm = searchTerm.toLowerCase();

        return blogs.filter(blog =>
            blog.title.toLowerCase().includes(lowerSearchTerm) ||
            blog.excerpt.toLowerCase().includes(lowerSearchTerm) ||
            blog.content.toLowerCase().includes(lowerSearchTerm) ||
            blog.tags.some(tag => tag.toLowerCase().includes(lowerSearchTerm)) ||
            blog.seoKeywords.some(keyword => keyword.toLowerCase().includes(lowerSearchTerm))
        );
    } catch (error) {
        console.error('Error searching blogs:', error);
        throw error;
    }
};

/**
 * Get featured blogs
 */
export const getFeaturedBlogs = async (): Promise<Blog[]> => {
    try {
        // Temporary fix: Get ALL blogs unfiltered and filter client-side to avoid the
        // composite index requirement. NOTE: passing a filter (e.g. { published: true })
        // here triggers the where()+orderBy() path in getAllBlogs, which needs a composite
        // index that may not exist and throws `failed-precondition`. So fetch unfiltered.
        const { blogs } = await getAllBlogs();
        return blogs
            .filter(blog => blog.published && blog.featured)
            .sort((a, b) =>
                new Date(b.publishedDate || 0).getTime() - new Date(a.publishedDate || 0).getTime()
            );
        
        // Optimized version (uncomment once Firestore index is built):
        // const { blogs } = await getAllBlogs({ featured: true, published: true });
        // return blogs;
    } catch (error) {
        console.error('Error getting featured blogs:', error);
        throw error;
    }
};

/**
 * Get blog by ID
 */
export const getBlogById = async (id: string): Promise<Blog | null> => {
    try {
        const docRef = doc(blogsCollection, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Blog;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting blog:', error);
        throw error;
    }
};

/**
 * Get blog by slug
 */
export const getBlogBySlug = async (slug: string): Promise<Blog | null> => {
    try {
        const blogsQuery = query(blogsCollection, where('slug', '==', slug));
        const querySnapshot = await getDocs(blogsQuery);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data()
            } as Blog;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting blog by slug:', error);
        throw error;
    }
};

/**
 * Create a new blog
 */
export const createBlog = async (blogData: Omit<Blog, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        // Handle featured image upload if provided as base64
        let featuredImageUrl = blogData.featuredImage;
        if (featuredImageUrl && featuredImageUrl.startsWith('data:')) {
            featuredImageUrl = await uploadBase64ImageToCloudinary(featuredImageUrl, 'blog_featured_images');
        }

        // Handle gallery images upload if provided as base64
        let galleryUrls = blogData.gallery || [];
        if (galleryUrls.length > 0) {
            const uploadPromises = galleryUrls.map(async (image, index) => {
                if (image.startsWith('data:')) {
                    return await uploadBase64ImageToCloudinary(image, 'blog_gallery_images');
                }
                return image;
            });
            galleryUrls = await Promise.all(uploadPromises);
        }

        // Handle author image upload if provided as base64
        let authorImageUrl = blogData.authorImage;
        if (authorImageUrl && authorImageUrl.startsWith('data:')) {
            authorImageUrl = await uploadBase64ImageToCloudinary(authorImageUrl, 'blog_author_images');
        }

        const docRef = await addDoc(blogsCollection, {
            ...blogData,
            featuredImage: featuredImageUrl,
            gallery: galleryUrls,
            authorImage: authorImageUrl,
            views: 0,
            likes: 0,
            comments: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        return docRef.id;
    } catch (error) {
        console.error('Error creating blog:', error);
        throw error;
    }
};

/**
 * Update a blog
 */
export const updateBlog = async (id: string, blogData: Partial<Omit<Blog, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
    try {
        // Handle featured image upload if provided as base64
        let featuredImageUrl = blogData.featuredImage;
        if (featuredImageUrl && featuredImageUrl.startsWith('data:')) {
            featuredImageUrl = await uploadBase64ImageToCloudinary(featuredImageUrl, 'blog_featured_images');
        }

        // Handle gallery images upload if provided as base64
        let galleryUrls = blogData.gallery;
        if (galleryUrls && galleryUrls.length > 0) {
            const uploadPromises = galleryUrls.map(async (image, index) => {
                if (image.startsWith('data:')) {
                    return await uploadBase64ImageToCloudinary(image, 'blog_gallery_images');
                }
                return image;
            });
            galleryUrls = await Promise.all(uploadPromises);
        }

        // Handle author image upload if provided as base64
        let authorImageUrl = blogData.authorImage;
        if (authorImageUrl && authorImageUrl.startsWith('data:')) {
            authorImageUrl = await uploadBase64ImageToCloudinary(authorImageUrl, 'blog_author_images');
        }

        const docRef = doc(blogsCollection, id);
        await updateDoc(docRef, {
            ...blogData,
            ...(featuredImageUrl && { featuredImage: featuredImageUrl }),
            ...(galleryUrls && { gallery: galleryUrls }),
            ...(authorImageUrl && { authorImage: authorImageUrl }),
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating blog:', error);
        throw error;
    }
};

/**
 * Delete a blog
 */
export const deleteBlog = async (id: string): Promise<void> => {
    try {
        const docRef = doc(blogsCollection, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting blog:', error);
        throw error;
    }
};

/**
 * Increment blog views
 */
export const incrementBlogViews = async (id: string): Promise<void> => {
    try {
        const blog = await getBlogById(id);
        if (blog) {
            const docRef = doc(blogsCollection, id);
            await updateDoc(docRef, {
                views: (blog.views || 0) + 1
            });
        }
    } catch (error) {
        console.error('Error incrementing blog views:', error);
        throw error;
    }
};

/**
 * Get all blog categories
 */
export const getAllBlogCategories = async (): Promise<BlogCategory[]> => {
    try {
        const querySnapshot = await getDocs(blogCategoriesCollection);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as BlogCategory));
    } catch (error) {
        console.error('Error getting blog categories:', error);
        throw error;
    }
};

/**
 * Create blog category
 */
export const createBlogCategory = async (categoryData: Omit<BlogCategory, 'id'>): Promise<string> => {
    try {
        const docRef = await addDoc(blogCategoriesCollection, categoryData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating blog category:', error);
        throw error;
    }
};

/**
 * Update blog category
 */
export const updateBlogCategory = async (id: string, categoryData: Partial<Omit<BlogCategory, 'id'>>): Promise<void> => {
    try {
        const docRef = doc(blogCategoriesCollection, id);
        await updateDoc(docRef, categoryData);
    } catch (error) {
        console.error('Error updating blog category:', error);
        throw error;
    }
};

/**
 * Delete blog category
 */
export const deleteBlogCategory = async (id: string): Promise<void> => {
    try {
        const docRef = doc(blogCategoriesCollection, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting blog category:', error);
        throw error;
    }
};

/**
 * Generate slug from title
 */
export const generateSlug = (title: string): string => {
    return title
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Get related blogs based on tags and category
 */
export const getRelatedBlogs = async (blogId: string, limit: number = 3): Promise<Blog[]> => {
    try {
        const blog = await getBlogById(blogId);
        if (!blog) return [];

        const { blogs } = await getAllBlogs({
            category: blog.category,
            published: true
        });

        // Filter out the current blog and find related ones
        const relatedBlogs = blogs
            .filter(b => b.id !== blogId)
            .map(b => ({
                ...b,
                relevanceScore: calculateRelevanceScore(blog, b)
            }))
            .sort((a, b) => b.relevanceScore - a.relevanceScore)
            .slice(0, limit);

        return relatedBlogs;
    } catch (error) {
        console.error('Error getting related blogs:', error);
        throw error;
    }
};

/**
 * Calculate relevance score between two blogs
 */
const calculateRelevanceScore = (blog1: Blog, blog2: Blog): number => {
    let score = 0;

    // Same category
    if (blog1.category === blog2.category) score += 5;

    // Same subcategory
    if (blog1.subcategory === blog2.subcategory) score += 3;

    // Same difficulty
    if (blog1.difficulty === blog2.difficulty) score += 2;

    // Common tags
    const commonTags = blog1.tags.filter(tag => blog2.tags.includes(tag));
    score += commonTags.length * 2;

    // Common related topics
    const commonTopics = blog1.relatedTopics.filter(topic => blog2.relatedTopics.includes(topic));
    score += commonTopics.length;

    return score;
};

/**
 * Update blog order
 */
const updateBlogOrder = async (id: string, order: number): Promise<void> => {
    try {
        const blogRef = doc(db, 'blogs', id);
        await updateDoc(blogRef, { order });
    } catch (error) {
        console.error('Error updating blog order:', error);
        throw error;
    }
};

/**
 * Move blog up in order
 */
export const moveBlogUp = async (id: string): Promise<void> => {
    try {
        const { blogs } = await getAllBlogs();
        const sortedBlogs = blogs.sort((a, b) => (a.order || 0) - (b.order || 0));
        const currentIndex = sortedBlogs.findIndex(blog => blog.id === id);

        if (currentIndex > 0) {
            const currentBlog = sortedBlogs[currentIndex];
            const previousBlog = sortedBlogs[currentIndex - 1];

            // Swap orders
            if (currentBlog.id && previousBlog.id) {
                await updateBlogOrder(currentBlog.id, previousBlog.order || 0);
                await updateBlogOrder(previousBlog.id, currentBlog.order || 0);
            }
        }
    } catch (error) {
        console.error('Error moving blog up:', error);
        throw error;
    }
};

/**
 * Move blog down in order
 */
export const moveBlogDown = async (id: string): Promise<void> => {
    try {
        const { blogs } = await getAllBlogs();
        const sortedBlogs = blogs.sort((a, b) => (a.order || 0) - (b.order || 0));
        const currentIndex = sortedBlogs.findIndex(blog => blog.id === id);

        if (currentIndex < sortedBlogs.length - 1) {
            const currentBlog = sortedBlogs[currentIndex];
            const nextBlog = sortedBlogs[currentIndex + 1];

            // Swap orders
            if (currentBlog.id && nextBlog.id) {
                await updateBlogOrder(currentBlog.id, nextBlog.order || 0);
                await updateBlogOrder(nextBlog.id, currentBlog.order || 0);
            }
        }
    } catch (error) {
        console.error('Error moving blog down:', error);
        throw error;
    }
}; 